import React, {useMemo, useState} from 'react';

import {humanEnumeration} from '../../../../components/utils';
import {useCharacter} from '../character-context';
import type {Catalog} from '../model/catalog';
import type {Character} from '../model/character';
import {optionLabel} from '../model/choice-options';
import {CharacterFeatures, FeatureInstance, flattenChoices, flattenFeatures} from '../model/feature-tree';
import {isPreparedOnSheet} from '../model/spell-preparation';
import {ExpandableRow} from './expandable-row';
import {FilterChip, FilterChips} from './filter-chips';
import {UsageCount} from './usage-tracker';

import css from './sheet.module.css';

type FeatureGroup = 'class' | 'specie' | 'feat';
type FeatureFilter = 'all' | FeatureGroup;

type FeatureEntry = {
	instance: FeatureInstance;
	group: FeatureGroup;
	/** откуда особенность: класс, вид или происхождение */
	sourceTitle?: string;
};

const GROUP_TITLES: Record<FeatureGroup, string> = {
	class: 'Особенности классов',
	specie: 'Особенности вида',
	feat: 'Черты',
};

const FILTERS: FilterChip<FeatureFilter>[] = [
	{value: 'all', label: 'Все'},
	{value: 'class', label: 'Классовые'},
	{value: 'specie', label: 'Вида'},
	{value: 'feat', label: 'Черты'},
];

export const FeaturesTab: React.FC = () => {
	const {catalog, features, character} = useCharacter();
	const [filter, setFilter] = useState<FeatureFilter>('all');
	const entries = useMemo(() => featureEntries(features, catalog), [features, catalog]);
	const useKeys = useMemo(() => useKeysByFeature(character), [character]);

	const chips = FILTERS.map((chip) => ({
		...chip,
		count: chip.value === 'all' ? undefined : entries.filter((entry) => entry.group === chip.value).length,
	}));
	const groups = (['class', 'specie', 'feat'] as FeatureGroup[])
		.filter((group) => filter === 'all' || filter === group)
		.map((group) => ({group, entries: entries.filter((entry) => entry.group === group)}))
		.filter((group) => group.entries.length);

	return (
		<>
			<FilterChips<FeatureFilter> label="Вид особенностей" items={chips} value={filter} onChange={setFilter} />
			{groups.map(({group, entries: groupEntries}) => (
				<section key={group} className={css.tabSection}>
					<h3 className={css.groupTitle}>{GROUP_TITLES[group]}</h3>
					<ul className={css.rows}>
						{groupEntries.map((entry) => (
							<FeatureRow
								key={entry.instance.key}
								entry={entry}
								useKeys={useKeys.get(entry.instance.key)}
							/>
						))}
					</ul>
				</section>
			))}
		</>
	);
};

const FeatureRow: React.FC<{entry: FeatureEntry; useKeys?: string[]}> = ({entry, useKeys}) => {
	const {catalog} = useCharacter();
	const {instance, sourceTitle} = entry;
	const selected = flattenChoices(
		instance.choices.filter((choice) => !isPreparedOnSheet(choice, instance.feature)),
	).flatMap((choice) => choice.selected.map((key) => optionLabel(choice.group, key, catalog)));
	const note = [sourceTitle, instance.owner.classId && `${instance.level}-й уровень`].filter(Boolean).join(', ');

	return (
		<ExpandableRow
			title={instance.feature.title}
			note={note}
			details={selected.length > 0 && <span className={css.muted}>выбрано: {humanEnumeration(selected)}</span>}
			aside={useKeys?.map((useKey) => (
				<UsageCount key={useKey} useKey={useKey} />
			))}
			description={instance.feature.description ? {kind: 'text', text: instance.feature.description} : undefined}
		/>
	);
};

function featureEntries(features: CharacterFeatures, catalog: Catalog): FeatureEntry[] {
	const seen = new Set<string>();
	const entryOf = (instance: FeatureInstance, group: FeatureGroup, sourceTitle?: string): FeatureEntry[] => {
		const isFeat = !!instance.feature.featId;
		const repeatKey = `${instance.owner.key}/${instance.feature.id}`;
		if (!instance.choices.length && seen.has(repeatKey)) return [];
		seen.add(repeatKey);
		return [
			{
				instance,
				group: isFeat ? 'feat' : group,
				sourceTitle: isFeat ? catalog.getFeat(instance.feature.featId!)?.title : sourceTitle,
			},
		];
	};

	return [
		...features.classes.flatMap(({classData, features: classFeatures}) =>
			flattenFeatures(classFeatures).flatMap((instance) => entryOf(instance, 'class', classData.title)),
		),
		...flattenFeatures(features.specie).flatMap((instance) => entryOf(instance, 'specie')),
		...flattenFeatures(features.origin)
			.filter((instance) => instance.feature.featId)
			.flatMap((instance) => entryOf(instance, 'feat')),
	];
}

function useKeysByFeature(character: Character): Map<string, string[]> {
	const keys = new Map<string, Set<string>>();
	const add = (featureKey: string, useKey: string) => {
		if (!character.limitedUse(useKey)) return;
		keys.set(featureKey, (keys.get(featureKey) || new Set()).add(useKey));
	};
	for (const resource of character.resources) add(resource.featureKey, resource.key);
	for (const action of character.actions) {
		add(action.featureKey, action.resource ? `resource:${action.resource}` : action.key);
	}
	return new Map([...keys].map(([featureKey, useKeys]) => [featureKey, [...useKeys]]));
}
